import { useState } from "react";
import { Dialog } from "../components";
import { GameLayout } from "./shared/GameLayout";
import {
  BallotTally,
  GameArt,
  PersonalOutcomeLine,
  PrivateInfo,
  SubmitButton,
  TargetAction,
  draftScope,
} from "./shared/PresenterParts";
import { useDraft } from "./shared/drafts";
import { personalOutcome } from "./outcome";
import { offlineSeatIds } from "./public-presentation";
import { PrivateTrigger } from "./shared/PrivatePanel";
import { PlayerChoices } from "./shared/PlayerChoices";
import {
  AccusationOutcome,
  accusationResultTitle,
} from "./shared/AccusationOutcome";
import { playerName, type PlayProps } from "./types";
import { GameIcon } from "./shared/GameIcons";
import { publicTableModel } from "./public-presentation";
function ConversationAction({
  props,
  answer = false,
}: {
  props: PlayProps;
  answer?: boolean;
}) {
  const { state, send } = props,
    p = state.game.public,
    spoken = p.conversationMode !== "text",
    players = p.players ?? [],
    scope = draftScope(state),
    kind = answer ? "answer" : "question",
    [choice, setChoice] = useDraft<string | null>(scope, `${kind}:target`, null),
    [text, setText] = useDraft(scope, `${kind}:text`, "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if ((answer || choice) && (spoken || text.trim()))
          send({
            type: answer ? "answer" : "question",
            ...(!answer ? { targetId: choice } : {}),
            ...(text.trim() ? { text: text.trim() } : {}),
          });
      }}
    >
      {!answer ? (
        <PlayerChoices
          players={players}
          eligibleIds={
            state.game.private.eligibleTargetIds ??
            players
              .filter((v: any) => v.id !== state.playerId)
              .map((v: any) => v.id)
          }
          selectedId={choice}
          onSelect={setChoice}
          offlineIds={offlineSeatIds(state.players)}
        />
      ) : null}
      {spoken ? (
        <details className="play-optional-note" data-tour="note">
          <summary>Add an optional note</summary>
          <label>
            {answer ? "Answer note" : "Question note"}
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={160}
            />
          </label>
        </details>
      ) : (
        <label>
          {answer ? "Your answer" : "Your question"}
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              answer
                ? "Answer without giving the place away…"
                : "Ask something about the location…"
            }
            maxLength={160}
            required
            autoComplete="off"
          />
        </label>
      )}
      <SubmitButton
        disabled={(!answer && choice === null) || (!spoken && !text.trim())}
      >
        {!answer && choice === null
          ? "Pick who to ask"
          : spoken
          ? answer
            ? "Answered aloud"
            : "Asked aloud"
          : answer
            ? "Send answer"
            : "Ask question"}
      </SubmitButton>
    </form>
  );
}
function GuessLocation({ props }: { props: PlayProps }) {
  const scope = draftScope(props.state),
    [selected, setSelected] = useDraft<string | null>(scope, "guess:location", null),
    [confirm, setConfirm] = useDraft(scope, "guess:confirm", false);
  const locations = props.state.game.public.locations ?? [];
  return (
    <>
      <h4 className="private-instruction-title">Guess the location</h4>
      <p className="private-instruction">A wrong guess ends the game.</p>
      <div className="play-location-choices" data-tour="location-guess">
        {locations.map((v: any) => (
          <button
            type="button"
            className="play-location-choice"
            key={v.id}
            aria-pressed={selected === v.id}
            onClick={() => {
              setSelected(v.id);
              setConfirm(false);
            }}
          >
            {v.name}
          </button>
        ))}
      </div>
      {confirm ? (
        <>
          <p>
            Lock in{" "}
            <strong>
              {locations.find((v: any) => v.id === selected)?.name}
            </strong>
            ?
          </p>
          <SubmitButton
            type="button"
            onClick={() => props.send({ type: "guess", locationId: selected })}
          >
            Confirm final guess
          </SubmitButton>
          <button className="text-button" onClick={() => setConfirm(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button
          className="primary full"
          disabled={!selected}
          onClick={() => setConfirm(true)}
        >
          {selected ? "Guess location" : "Pick a location"}
        </button>
      )}
    </>
  );
}
/** Tap-to-strike notes on the location shortlist: local to this tab and match,
 * kept in sessionStorage, never sent or shown to anyone else. */
function useStruckLocations(matchId: string | null, playerId: string | null) {
  const key = `after-hours:alibi-struck:v1:${matchId ?? ""}:${playerId ?? ""}`;
  const read = (): string[] => {
    try {
      const v = JSON.parse(sessionStorage.getItem(key) ?? "[]");
      return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  };
  const [state, setState] = useState<{ key: string; ids: string[] }>(() => ({
    key,
    ids: typeof sessionStorage === "undefined" ? [] : read(),
  }));
  const ids = state.key === key ? state.ids : read();
  const toggle = (id: string) => {
    const next = ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id];
    try {
      sessionStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* Storage blocked: the strike lasts for this view only. */
    }
    setState({ key, ids: next });
  };
  return [ids, toggle] as const;
}
export function Alibi(props: PlayProps) {
  const { state, isDisplay, send } = props,
    g = state.game,
    p = g.public,
    s = g.private,
    players = p.players ?? [],
    n = (id: string) => playerName(players, id),
    can = (a: string) =>
      !isDisplay && !state.isSpectator && g.legalActions.includes(a),
    [locationsOpen, setLocationsOpen] = useState(false),
    exchange = p.exchanges?.at(-1),
    voting = ["vote", "runoff"].includes(g.phase),
    model = publicTableModel(g, state.players ?? []),
    publicAudience = isDisplay || state.isSpectator,
    offline = offlineSeatIds(state.players),
    [struck, toggleStruck] = useStruckLocations(
      state.matchId,
      publicAudience ? null : state.playerId,
    ),
    outcome = personalOutcome(g, state.playerId, publicAudience),
    who = (id: string) =>
      offline.includes(id) ? `${n(id)} (offline)` : n(id);
  const title = g.finished
    ? accusationResultTitle(g)
    : g.phase === "question"
      ? can("question")
        ? "Ask your question."
        : `${n(p.turnPlayerId)} is asking a question.`
      : g.phase === "answer"
        ? can("answer")
          ? "Your turn to answer."
          : `${n(p.answerPlayerId)} is answering.`
        : voting
          ? g.phase === "runoff"
            ? "Break the tie."
            : "Who’s the spy?"
          : can("guess")
            ? "Guess the location."
            : `${n(p.accusedId)} is guessing the location.`;
  return (
    <GameLayout
      {...props}
      title={title}
      privateScope="match"
      progressLabel="voted"
      publicTitle={
        g.phase === "question"
          ? `${n(p.turnPlayerId)} is asking a question.`
          : g.phase === "answer"
            ? `${n(p.answerPlayerId)} is answering.`
            : g.phase === "guess" && !g.finished
              ? `${n(p.accusedId)} is guessing the location.`
              : title
      }
      phaseLabel={
        g.finished
          ? "Result"
          : voting
            ? g.phase === "runoff"
              ? "Runoff vote"
              : "Vote"
            : g.phase === "guess"
              ? "Final guess"
              : `Question round ${p.questionPass ?? g.round} of ${p.questionRounds ?? 1}`
      }
      phaseNote={
        voting && model.progress
          ? `${model.progress.submitted} of ${model.progress.eligible} voted`
          : null
      }
      board={
        <>
          <GameArt
            kind="alibi"
            variant={
              voting ||
              g.finished ||
              g.phase === "guess" ||
              (g.phase === "question" && can("question"))
                ? "stamp"
                : "band"
            }
          />
          <button
            type="button"
            className="play-chip-button play-location-reference"
            data-tour="locations"
            onClick={() => setLocationsOpen(true)}
          >
            <GameIcon name="pin" size={16} />
            Possible locations · {p.locations?.length ?? 0}
            {struck.length > 0 && !publicAudience ? (
              <span className="play-chip">{struck.length} struck</span>
            ) : null}
          </button>
          {exchange &&
          !g.finished &&
          g.phase !== "guess" &&
          !(g.phase === "question" && can("question")) ? (
            <div className="alibi-exchange" data-tour="exchanges">
              <h2 className="play-board-heading">
                {g.phase === "answer" ? "The question" : "Last exchange"}
              </h2>
              <div className="bubble is-question">
                <span className="bubble-who">
                  {who(exchange.questionerId)} → {who(exchange.answererId)}
                </span>
                <p className="play-excerpt">
                  {exchange.question.text ??
                    exchange.question.note ??
                    "Asked aloud"}
                </p>
              </div>
              {exchange.answer ? (
                <div className="bubble is-answer">
                  <span className="bubble-who">{n(exchange.answererId)}</span>
                  <p className="play-excerpt">
                    {exchange.answer.text ??
                      exchange.answer.note ??
                      "Answered aloud"}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
          {g.phase === "guess" && !g.finished ? (
            <BallotTally
              players={players}
              ballots={p.revealedBallots ?? []}
              viewerId={publicAudience ? null : state.playerId}
              heading="The vote"
              compact
            />
          ) : null}
          {g.finished ? (
            <>
              <PersonalOutcomeLine outcome={outcome} />
              <AccusationOutcome
                game={g}
                expanded={publicAudience}
                viewerId={publicAudience ? null : state.playerId}
              />
            </>
          ) : null}
          {locationsOpen ? (
            <Dialog
              title="Possible locations"
              onClose={() => setLocationsOpen(false)}
            >
              <p className="play-help-copy">
                One of these is the secret location. Only the spy doesn’t know
                which.
                {publicAudience
                  ? null
                  : " Tap a place to strike it off — only you see this."}
              </p>
              <ul
                className="play-location-reference-list"
                data-tour="location-list"
              >
                {(p.locations ?? []).map((v: any) =>
                  publicAudience ? (
                    <li key={v.id}>{v.name}</li>
                  ) : (
                    <li key={v.id}>
                      <button
                        type="button"
                        className="play-location-strike"
                        aria-pressed={struck.includes(v.id)}
                        onClick={() => toggleStruck(v.id)}
                      >
                        <span>{v.name}</span>
                        <span className="sr-only">
                          {struck.includes(v.id) ? ", struck off" : ""}
                        </span>
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </Dialog>
          ) : null}
        </>
      }
      action={
        can("question") ? (
          <ConversationAction props={props} />
        ) : can("answer") ? (
          <ConversationAction props={props} answer />
        ) : can("vote") ? (
          <TargetAction
            players={players}
            ids={s.eligibleTargetIds ?? []}
            type="vote"
            label="Submit secret vote"
            emptyLabel="Pick who you think is the spy"
            send={send}
            scope={draftScope(state)}
            offlineIds={offline}
          />
        ) : g.phase === "guess" && can("guess") ? (
          <PrivateTrigger>Open your final guess</PrivateTrigger>
        ) : null
      }
      privateContent={
        <PrivateInfo state={state}>
          {s.role === "spy" ? (
            <p className="private-hint">
              Location unknown. Listen for clues and blend in.
            </p>
          ) : null}
        </PrivateInfo>
      }
      privateAction={can("guess") ? <GuessLocation props={props} /> : undefined}
    />
  );
}
